import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

export const TIVIGO_SITE_URL = 'https://tivigoiptv.info';
export const TIVIGO_BASE_TITLE = 'Tivigo IPTV | Canlı TV, Film ve Dizi Platformu';
export const TIVIGO_SEO_KEYWORDS = [
  'Tivigo',
  'Tivigo IPTV',
  'IPTV Türkiye',
  'canlı TV',
  'internet televizyonu',
  'IPTV paketleri',
  'IPTV üyelik',
  'IPTV deneme',
  'Tivigo giriş',
  'Tivigo paketler',
  'Tivigo uygulama',
  'Android IPTV',
  'iOS IPTV',
  'Windows IPTV',
  'Smart TV IPTV',
  'film izle',
  'dizi izle',
].join(', ');

// public/index.html ile aynı metinler; sayfa açılınca burası aynı değerleri yeniden yazar.
const LANDING_DESCRIPTION = "Tivigo IPTV ile canlı TV, Süper Lig, film ve dizileri Android, iOS, Windows, macOS ve Smart TV'de izleyin. Ücretsiz test, 14 gün koşulsuz iade, 7/24 Türkçe destek.";
const SOCIAL_DESCRIPTION = 'Canlı TV, Süper Lig, film ve diziler tüm cihazlarında. Ücretsiz test, 14 gün koşulsuz iade, 7/24 Türkçe destek.';
const SOCIAL_IMAGE = `${TIVIGO_SITE_URL}/assets/images/tivigo-og-1200x630.png`;
const INDEX_ROBOTS = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const PRIVATE_ROBOTS = 'noindex, nofollow, noarchive';

const routeSeo = (pathname, username) => {
  if (pathname === '/') {
    return {
      title: TIVIGO_BASE_TITLE,
      description: LANDING_DESCRIPTION,
      canonicalPath: '/',
      robots: INDEX_ROBOTS,
    };
  }

  if (pathname === '/login' || pathname === '/giris') {
    return {
      title: 'Tivigo Giriş | Hesabınıza Giriş Yapın',
      description: 'Tivigo hesabınıza güvenli şekilde giriş yaparak üyelik ve yayın bilgilerinizi yönetin.',
      canonicalPath: '/login',
      robots: PRIVATE_ROBOTS,
    };
  }

  if (pathname === '/register' || pathname === '/kayit-ol' || pathname === '/kayit' || pathname === '/test-kayit') {
    return {
      title: 'Tivigo Kayıt | Hesabınızı Oluşturun',
      description: 'Tivigo hesabınızı oluşturun ve üyelik işlemlerinizi güvenli şekilde tamamlayın.',
      canonicalPath: pathname === '/kayit' || pathname === '/test-kayit' ? pathname : '/register',
      robots: PRIVATE_ROBOTS,
    };
  }

  if (pathname === '/forgot-password' || pathname === '/sifremi-unuttum') {
    return {
      title: 'Şifremi Unuttum | Tivigo IPTV',
      description: 'Tivigo hesabınızın şifresini güvenli adımlarla yenileyin.',
      canonicalPath: '/forgot-password',
      robots: PRIVATE_ROBOTS,
    };
  }

  if (pathname === '/blog' || pathname.startsWith('/blog/')) {
    // Başlık, açıklama ve JSON-LD yazı verisi gelince BlogPage tarafından yazılır.
    const canonicalPath = pathname.endsWith('/') ? pathname : `${pathname}/`;
    return {
      title: 'Tivigo Blog | IPTV rehberleri ve ipuçları',
      description: 'IPTV kurulum rehberleri, cihaz ayarları, yayın kalitesi ipuçları ve Tivigo haberleri.',
      canonicalPath,
      robots: INDEX_ROBOTS,
    };
  }

  if (pathname.startsWith('/dashboard')) {
    return {
      title: username ? `${TIVIGO_BASE_TITLE} - ${username}` : 'Tivigo Hesap Merkezi',
      description: 'Tivigo üyelik, cihaz, paket ve hesap işlemlerini yönetin.',
      canonicalPath: '/dashboard',
      robots: PRIVATE_ROBOTS,
    };
  }

  return {
    title: TIVIGO_BASE_TITLE,
    description: LANDING_DESCRIPTION,
    canonicalPath: pathname,
    robots: PRIVATE_ROBOTS,
  };
};

const setMeta = (attribute, key, content) => {
  let meta = document.head.querySelector(`meta[${attribute}="${key}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', content);
};

const setLink = (selector, attributes) => {
  let link = document.head.querySelector(selector);
  if (!link) {
    link = document.createElement('link');
    document.head.appendChild(link);
  }
  Object.entries(attributes).forEach(([key, value]) => link.setAttribute(key, value));
};

function TivigoSeo({ userInfo }) {
  const location = useLocation();
  const username = userInfo?.user_info?.username || userInfo?.user?.username || '';

  useEffect(() => {
    const seo = routeSeo(location.pathname, username);
    const canonicalUrl = new URL(seo.canonicalPath, `${TIVIGO_SITE_URL}/`).toString();

    document.title = seo.title;
    document.documentElement.lang = 'tr';

    setMeta('name', 'description', seo.description);
    setMeta('name', 'keywords', TIVIGO_SEO_KEYWORDS);
    setMeta('name', 'author', 'Tivigo IPTV');
    setMeta('name', 'robots', seo.robots);
    setMeta('name', 'googlebot', seo.robots);
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:site_name', 'Tivigo IPTV');
    setMeta('property', 'og:locale', 'tr_TR');
    setMeta('property', 'og:title', seo.title);
    setMeta('property', 'og:description', location.pathname === '/' ? SOCIAL_DESCRIPTION : seo.description);
    setMeta('property', 'og:url', canonicalUrl);
    setMeta('property', 'og:image', SOCIAL_IMAGE);
    setMeta('property', 'og:image:alt', 'Tivigo IPTV – canlı TV, film ve dizi');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:title', seo.title);
    setMeta('name', 'twitter:description', location.pathname === '/' ? SOCIAL_DESCRIPTION : seo.description);
    setMeta('name', 'twitter:image', SOCIAL_IMAGE);
    setMeta('name', 'twitter:image:alt', 'Tivigo IPTV – canlı TV, film ve dizi');

    setLink('link[rel="canonical"]', { rel: 'canonical', href: canonicalUrl });
    setLink('link[rel="alternate"][hreflang="tr-TR"]', { rel: 'alternate', hreflang: 'tr-TR', href: canonicalUrl });
    setLink('link[rel="alternate"][hreflang="x-default"]', { rel: 'alternate', hreflang: 'x-default', href: canonicalUrl });
  }, [location.pathname, username]);

  return null;
}

export default TivigoSeo;
